/**
 * 選択肢の表示条件フィルタ（R3 物語接続）
 *
 * dialog の choices と line_dialog の reply options は、スキーマ上
 * `condition?: BranchPreset` を持てる（凍結済み・既存フィールド）。
 * この条件を「UIへ公開する時点」で評価し、満たさない選択肢を非表示にする。
 *
 * 重要: 表示側（overlay / LineDialogView）と選択解決側（StepEngine の
 * selectDialogChoice / selectLineDialogChoice）が **同一のフィルタ結果** を
 * 参照しないと、選択 index がズレて別の選択肢が選ばれる事故になる。
 * そのため両者は必ずこの関数を通し、同じ可視リストに対して index を解釈する。
 *
 * 評価は branchEvaluator を「読むだけ」で再利用する（state を渡して
 * applyBranchPreset を呼ぶのみ・branchEvaluator 本体は不変）。
 */

import type { BranchPreset, RunState } from 'shared';
import { BranchEvaluator } from './branchEvaluator';

/**
 * condition を持ち得る選択肢の最小形。
 * condition の型は `unknown` にしておく（呼び出し側の choices 配列は
 * `condition?: BranchPreset` だったり、stepEngine 内部の緩い `condition?: unknown`
 * だったりするため、どちらも構造的に満たせるようにする）。
 * 評価直前に BranchPreset として解釈する。
 */
export interface ConditionedChoice {
  condition?: unknown;
}

/**
 * choices を condition で絞り込む。
 *
 * - condition 未指定の選択肢は常に表示。
 * - condition 指定ありは applyBranchPreset(condition) === true のときだけ表示。
 *
 * 安全フォールバック（詰み防止）:
 *   (a) フィルタ結果が0件になったら、condition 無しの選択肢があればそれらを返す。
 *   (b) それも無ければ全選択肢を返す（条件で全消ししてソフトロックさせない）。
 *
 * 既存シナリオ（どの選択肢も condition を持たない）では、全選択肢が
 * 「condition 無し＝常に表示」に該当するため、入力と同一配列順で全件返る
 * ＝完全な回帰非破壊。
 */
export function filterChoicesByCondition<T extends ConditionedChoice>(
  choices: readonly T[] | undefined,
  state: RunState
): T[] {
  if (!Array.isArray(choices) || choices.length === 0) {
    return [];
  }

  const evaluator = new BranchEvaluator(state);
  const visible = choices.filter((choice) => {
    // condition 未指定は常に表示（既存シナリオはこの経路で全件通過）。
    if (!choice.condition) {
      return true;
    }
    // applyBranchPreset が true のときだけ表示。
    // 未知の preset 型は undefined を返すため、その場合は安全側で非表示にせず
    // 表示する（条件が解釈できないことを理由にプレイヤーを詰ませない）。
    const result = evaluator.applyBranchPreset(choice.condition as BranchPreset);
    return result !== false;
  });

  if (visible.length > 0) {
    return visible;
  }

  // フォールバック(a): condition 無しの選択肢だけでも出す。
  const unconditional = choices.filter((choice) => !choice.condition);
  if (unconditional.length > 0) {
    return unconditional;
  }

  // フォールバック(b): それも無ければ全件出す（ソフトロック回避）。
  return [...choices];
}
